/* FilterableMultipleSelectListControlComponent.js */

"use strict";

const _ = require("lodash");
const MultipleSelectListControlComponent = require("./MultipleSelectListControlComponent");
const Renderer = require("./FilterableMultipleSelectListControlRenderer");

/**
 * Enhanced Multiple Select List Control with built-in search/filter functionality.
 * Combines the multi-select list capabilities with search filtering from ComboBox.
 *
 * @class FilterableMultipleSelectListControlComponent
 * @extends {MultipleSelectListControlComponent}
 *
 * @schema.name Filterable Multiple Select List Control
 * @schema.package @gluenetworks/select_list
 * @schema.packageIdentifier FilterableMultiple
 * @schema.namespace require("@gluenetworks/steve_ui_core").Control.OptionSelection.Multiple.FilterableSelectList
 * @schema.events ["Options", "Disabled", "Value", "Visible", "Invalid", "DarkTheme", "Focus", "Blur", "SetAttribute", "UserValue", "UserFocus", "UserBlur", "UserKeyDown", "FilterChange", "ShowFilter", "Placeholder"]
 *
 * @schema.configuration.showFilter.name showFilter
 * @schema.configuration.showFilter.type boolean
 * @schema.configuration.showFilter.default true
 * @schema.configuration.showFilter.required false
 *
 * @schema.configuration.placeholder.name placeholder
 * @schema.configuration.placeholder.type string
 * @schema.configuration.placeholder.default "Search..."
 * @schema.configuration.placeholder.required false
 *
 * @schema.configuration.filterDelay.name filterDelay
 * @schema.configuration.filterDelay.type number
 * @schema.configuration.filterDelay.default 300
 * @schema.configuration.filterDelay.required false
 *
 * @schema.configuration.caseSensitive.name caseSensitive
 * @schema.configuration.caseSensitive.type boolean
 * @schema.configuration.caseSensitive.default false
 * @schema.configuration.caseSensitive.required false
 *
 * @schema.configuration.sortAlphabetically.name sortAlphabetically
 * @schema.configuration.sortAlphabetically.type boolean
 * @schema.configuration.sortAlphabetically.default false
 * @schema.configuration.sortAlphabetically.required false
 *
 */
class FilterableMultipleSelectListControlComponent extends MultipleSelectListControlComponent {
    constructor(configOptions) {
        super(configOptions);

        // Polymorphism handling -> Local _createRenderer method called by parent class
        // this._renderer = new Renderer(this);
        // this._registerListeners();
    }

    _createRenderer(configOptions) {
        // Configuration options for filter control
        this._DEFAULT_PLACEHOLDER = "Search...";
        this._showFilter = _.get(configOptions, "showFilter", true);
        this._placeholder = _.get(configOptions, "placeholder", this._DEFAULT_PLACEHOLDER);
        this._filterDelay = _.get(configOptions, "filterDelay", 300);
        this._caseSensitive = _.get(configOptions, "caseSensitive", false);
        this._sortAlphabetically = _.get(configOptions, "sortAlphabetically", false);
        this._filterValue = "";
        this._filteredOptions = this._options || [];
        this._filterTimeout = null;

        // Renderer for filter control
        this._renderer = new Renderer(this);
    }

    _registerListeners() {
        super._registerListeners();
    }

    _unregisterListeners() {
        super._unregisterListeners();
        if (this._filterTimeout) {
            clearTimeout(this._filterTimeout);
        }
    }

    _applyFilter() {
        let filtered;

        if (!this._filterValue || !this._filterValue.trim()) {
            filtered = this._options || [];
        } else {
            const searchTerm = this._caseSensitive
                ? this._filterValue
                : this._filterValue.toLowerCase();
            const searchRegex = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const regexOpts = this._caseSensitive ? "g" : "gi";

            filtered = (this._options || []).filter((option) => {
                const label = this._caseSensitive
                    ? option.label
                    : (option.label || "").toLowerCase();
                return new RegExp(searchRegex, regexOpts).test(label);
            });
        }

        // Always sort if sortAlphabetically is enabled, regardless of filter
        if (this._sortAlphabetically) {
            filtered = [...filtered].sort((a, b) =>
                (a.label || "").toLowerCase().localeCompare((b.label || "").toLowerCase())
            );
        }

        return filtered;
    }

    shutdown() {
        this._unregisterListeners();
        this._showFilter = undefined;
        this._placeholder = undefined;
        this._filterDelay = undefined;
        this._caseSensitive = undefined;
        this._sortAlphabetically = undefined;
        this._filterValue = undefined;
        this._filteredOptions = undefined;
        this._DEFAULT_PLACEHOLDER = undefined;
        return super.shutdown();
    }

    setFilterValue(newVal) {
        this._filterValue = newVal || "";
        if (this._filterTimeout) {
            clearTimeout(this._filterTimeout);
        }

        this._filterTimeout = setTimeout(() => {
            this._filteredOptions = this._applyFilter();
            this.emit("FilterChange", this._filterValue, this._filteredOptions);
        }, this._filterDelay);

        return this;
    }

    getFilterValue() {
        return this._filterValue;
    }

    get filterValue() {
        return this._filterValue;
    }

    set filterValue(newVal) {
        this.setFilterValue(newVal);
    }

    getFilteredOptions() {
        return this._filteredOptions;
    }

    get filteredOptions() {
        return this._filteredOptions;
    }

    setShowFilter(newVal) {
        this._showFilter = !!newVal;
        this.emit("ShowFilter", this._showFilter);
        return this;
    }

    get showFilter() {
        return this._showFilter;
    }

    set showFilter(newVal) {
        this.setShowFilter(newVal);
    }

    get placeholder() {
        return this._placeholder;
    }

    set placeholder(newVal) {
        this._placeholder = newVal || this._DEFAULT_PLACEHOLDER;
    }

    setCaseSensitive(newVal) {
        this._caseSensitive = !!newVal;
        this._filteredOptions = this._applyFilter();
        this.emit("FilterChange", this._filterValue, this._filteredOptions);
        return this;
    }

    get caseSensitive() {
        return this._caseSensitive;
    }

    set caseSensitive(newVal) {
        this.setCaseSensitive(newVal);
    }

    clearFilter() {
        return this.setFilterValue("");
    }

    setSortAlphabetically(newVal) {
        this._sortAlphabetically = !!newVal;
        this._filteredOptions = this._applyFilter();
        this.emit("FilterChange", this._filterValue, this._filteredOptions);
        return this;
    }

    get sortAlphabetically() {
        return this._sortAlphabetically;
    }

    set sortAlphabetically(newVal) {
        this.setSortAlphabetically(newVal);
    }

    setOptions(newVal) {
        super.setOptions(newVal);
        this._filteredOptions = this._applyFilter();
        this.emit("FilterChange", this._filterValue, this._filteredOptions);
        return this;
    }
}

module.exports = FilterableMultipleSelectListControlComponent;
